"use client"

import { useState } from "react";
import { subscribeToNewsletter } from "@/lib/newsletter-action";

interface NewsletterSignupProps {
  title?: string;
  description?: string;
}

export default function NewsletterSignup({ title, description }: NewsletterSignupProps) {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [message, setMessage] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus("loading");

    const result = await subscribeToNewsletter(email);

    if (result.success) {
      setStatus("success");
      setEmail("");
    } else if (result.message === "ALREADY_SUBSCRIBED") {
      setStatus("error");
      setMessage("Your email is already part of our inner circle.");
    } else {
      setStatus("error");
      setMessage(result.message);
    }
  };

  return (
    <div className="bg-zinc-900 text-white p-8 md:p-12 border border-white/10 relative overflow-hidden">
      {/* Decorative Background Element */}
      <div className="absolute top-0 right-0 w-64 h-64 bg-brand/10 blur-[100px] -mr-32 -mt-32" />

      <div className="relative z-10 max-w-2xl mx-auto text-center">
        <span className="text-brand font-bold uppercase tracking-widest text-[10px] mb-4 block">The Global Briefing</span>
        <h2 className="font-serif text-3xl md:text-5xl font-bold mb-6">{title || "Stay Ahead of the Shift."}</h2>
        <p className="text-zinc-400 font-serif text-lg md:text-xl mb-10 leading-relaxed">
          {description || "Join professionals receiving our rigorous, globally sourced insights every morning."}
        </p>

        {status === "success" ? (
          <div className="bg-brand/20 border border-brand p-6 rounded-sm">
            <h3 className="text-xl font-bold mb-2">You're in.</h3>
            <p className="text-sm text-zinc-300">Welcome to the inner circle. Check your inbox for the next briefing.</p>
          </div>
        ) : (
          <>
            <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-4 mb-2">
              <input
                type="email"
                required
                placeholder="Enter your professional email"
                className="flex-1 bg-white/5 border border-white/20 px-6 py-4 rounded-sm outline-none focus:border-brand focus:ring-1 focus:ring-brand transition-all text-white placeholder:text-zinc-500"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              <button
                type="submit"
                disabled={status === "loading"}
                className="bg-brand hover:bg-brand-dark px-10 py-4 font-bold uppercase tracking-widest text-xs transition-colors disabled:opacity-50"
              >
                {status === "loading" ? "Joining..." : "Subscribe"}
              </button>
            </form>
            {status === "error" && (
              <p className="text-red-500 text-xs font-bold uppercase tracking-widest text-left">{message}</p>
            )}
          </>
        )}

        <p className="text-[10px] text-zinc-600 mt-6 uppercase tracking-widest">
          No spam. Unsubscribe anytime. View our <span className="underline cursor-pointer">Privacy Policy</span>.
        </p>
      </div>
    </div>
  );
}
